import axios from 'axios';
import {
  adaptHockeyMutationResult,
  adaptRemindersRead,
  adaptStqStatus,
  createServerError,
  createUnauthorizedError,
  isUnauthenticatedGraphqlError,
  unwrapHockeyResult,
} from './adapters';
import { ME_BOOM_FIELD, MUTATIONS, QUERIES } from './operations';

const LEADERBOARD_ROUTES = new Set([
  'leaderboard',
  'leaderboardLocal',
  'seasonLeaderboard',
  'seasonLeaderboardLocal',
  'postSeasonLeaderboard',
  'postSeasonLeaderboardLocal',
]);

function buildLeaderboardInput(params = {}) {
  const input = {};
  if (params.challengeNumber != null) {
    input.challengeNumber = Number(params.challengeNumber);
  }
  if (params.season != null) {
    input.season = String(params.season);
  }
  if (params.mockResponse != null) {
    input.mockResponse = String(params.mockResponse);
  }
  return Object.keys(input).length ? input : undefined;
}

function remindersOptInFromData(data = {}) {
  const raw =
    data.attributes?.tnhc_user_reminders ??
    data.tnhc_user_reminders ??
    data.tnhcUserReminders ??
    data.hockeyRemindersOptIn;
  return raw === true || raw === 'true';
}

function buildGraphqlPayload({ method, route, data, params }) {
  const isWrite = method === 'POST' || method === 'PATCH';

  if (route === 'quiz') {
    if (isWrite) {
      return {
        query: MUTATIONS.quiz,
        operationName: 'RecordSkillTestingQuestion',
        variables: {
          input: {
            eventId: data.eventId,
            failed: Boolean(data.failed),
          },
        },
      };
    }
    return {
      query: QUERIES.quiz,
      operationName: 'SkillTestingQuestionStatus',
      variables: { eventId: params.eventId },
    };
  }

  if (route === 'notification') {
    // REST used POST for both read and write; treat presence of preference payload as write.
    const hasWritePayload =
      data &&
      (data.attributes?.tnhc_user_reminders != null ||
        data.tnhc_user_reminders != null ||
        data.tnhcUserReminders != null ||
        data.hockeyRemindersOptIn != null);

    if (hasWritePayload) {
      return {
        query: MUTATIONS.notification,
        operationName: 'UpdateHockeyReminders',
        variables: {
          input: {
            hockeyRemindersOptIn: remindersOptInFromData(data),
          },
        },
      };
    }
    return {
      query: QUERIES.notification,
      operationName: 'HockeyRemindersOptIn',
      variables: {},
    };
  }

  if (route === 'playerPicks' && isWrite) {
    return {
      query: MUTATIONS.playerPicks,
      operationName: 'SubmitHockeyPicks',
      variables: {
        input: {
          picks: (data.picks || []).map((pick) => ({
            setId: String(pick.setId),
            playerId: String(pick.playerId),
          })),
        },
      },
    };
  }

  if (route === 'user' && isWrite) {
    return {
      query: MUTATIONS.user,
      operationName: 'UpdateHockeyUser',
      variables: {
        input: {
          username: data.username,
        },
      },
    };
  }

  if (QUERIES[route]) {
    const variables = {};
    if (LEADERBOARD_ROUTES.has(route)) {
      const input = buildLeaderboardInput(params);
      if (input) variables.input = input;
    }
    return {
      query: QUERIES[route],
      variables,
    };
  }

  throw new Error(`No GraphQL mapping for route: ${route}`);
}

function adaptGraphqlData({ route, method, data, rawRequestData }) {
  const isWrite = method === 'POST' || method === 'PATCH';

  if (route === 'quiz') {
    const status = isWrite ? data?.recordSkillTestingQuestion : data?.me?.skillTestingQuestionStatus;
    return adaptStqStatus(status);
  }

  if (route === 'notification') {
    const hasWritePayload =
      rawRequestData &&
      (rawRequestData.attributes?.tnhc_user_reminders != null ||
        rawRequestData.tnhc_user_reminders != null ||
        rawRequestData.tnhcUserReminders != null ||
        rawRequestData.hockeyRemindersOptIn != null);

    if (hasWritePayload) {
      return Boolean(data?.updateHockeyReminders);
    }
    return adaptRemindersRead(data?.me?.hockeyRemindersOptIn);
  }

  if (route === 'playerPicks' && isWrite) {
    return unwrapHockeyResult(data?.submitHockeyPicks);
  }

  if (route === 'user' && isWrite) {
    return adaptHockeyMutationResult(data?.updateHockeyUser);
  }

  const field = ME_BOOM_FIELD[route];
  if (field) {
    return unwrapHockeyResult(data?.me?.[field]);
  }

  return data;
}

/**
 * POST to the federated gateway. Identity comes from the JWT only —
 * do not send x-loyalty-id / x-user-id / x-cognito-id.
 */
export async function makeGraphqlRequest({
  graphqlUrl,
  method = 'GET',
  language = 'en',
  platform = 'web',
  currentSession,
  route,
  data,
  params = {},
}) {
  const payload = buildGraphqlPayload({ method, route, data, params });

  try {
    const response = await axios({
      method: 'POST',
      url: graphqlUrl,
      data: {
        query: payload.query,
        variables: payload.variables || {},
        ...(payload.operationName ? { operationName: payload.operationName } : {}),
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${currentSession}`,
        'x-ui-region': 'CA',
        'x-ui-language': language,
        'x-ui-platform': platform,
      },
    });

    const { data: body } = response;

    if (body?.errors?.length) {
      if (isUnauthenticatedGraphqlError(body.errors)) {
        throw createUnauthorizedError();
      }
      throw createServerError();
    }

    return adaptGraphqlData({
      route,
      method,
      data: body?.data,
      rawRequestData: data,
    });
  } catch (err) {
    if (err?.status === 401 || err?.code === 500) {
      throw err;
    }
    if (err?.code === 'NotAllowed' || (err?.code && String(err.code).includes('hc.'))) {
      throw err;
    }
    if (err?.isNetworkError || err?.message === 'Network Error') {
      const networkErr = new Error('Network Error');
      networkErr.isNetworkError = true;
      throw networkErr;
    }
    if (err?.response?.status === 401) {
      throw createUnauthorizedError();
    }
    throw createServerError();
  }
}
